import { useContext, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { ThemeContext } from 'styled-components'
import { useAppSelector } from '@Hooks/ReduxHooks'

/**
 * WaselChat student mentor chat — vendor widget lifecycle.
 *
 * The vendor's embed.js reads `window.abwaabChat` (lowercase) once at execution
 * time, mounts an iframe (#abwaabchat-frame, z-index 2147483000), and exposes
 * `window.AbwaabChat.identify()` (capitalised) for identity changes. There is
 * NO destroy(): the vendor assumes logout is a full page load, but this app's
 * logout is a client-side redirect — so this component removes the frame
 * itself on the isAuth → false transition. Without that, the next account on
 * this browser inherits the previous student's chat.
 *
 * Everything is gated on the server-computed `auth.chat` payload: it is null
 * when the app_settings flag is off, the user is not a student, or the device
 * is unverified. `isEmbed` (admin impersonation) never gets a widget — an
 * admin viewing a student's portal must not be able to chat as them.
 */
const FRAME_ID = 'abwaabchat-frame'
const MOBILE_BOTTOM_OFFSET = 76 // clears the dashboard's sticky bottom nav (z-index 999)
const DESKTOP_BOTTOM_OFFSET = 24

const teardown = () => {
	document.getElementById(FRAME_ID)?.remove()
	document.querySelectorAll('script[data-waselchat]').forEach(s => s.remove())
	delete (window as any).abwaabChat
	delete (window as any).AbwaabChat
}

const WaselChat = () => {
	const { authReady, isAuth, chat } = useAppSelector(state => state.auth)
	const isEmbed = useAppSelector(state => state.utils.isEmbed)
	const theme = useContext(ThemeContext)
	const location = useLocation()

	const active = authReady && isAuth && Boolean(chat) && !isEmbed
	const direction = theme?.direction ?? 'ltr'

	// Hidden, not unmounted, during exams: the frame keeps its state and the
	// student is not mid-conversation-interrupted, it just cannot cover the
	// test player. embed.js exposes no hide API, but the iframe is our DOM.
	const suppressed = /^\/test\//.test(location.pathname)
	useEffect(() => {
		document.documentElement.classList.toggle('waselchat-hidden', suppressed)
		return () => document.documentElement.classList.remove('waselchat-hidden')
	}, [suppressed])

	useEffect(() => {
		if (!active || !chat) {
			teardown()
			return
		}

		const identity = { externalId: chat.externalId, identifierHash: chat.identifierHash }

		// The frame survives client-side navigation and identity refreshes; only
		// a changed identity needs telling, via the vendor's identify().
		if (document.getElementById(FRAME_ID)) {
			;(window as any).AbwaabChat?.identify?.(identity)
			return
		}

		// Settings must exist BEFORE the script runs — embed.js reads the
		// lowercase global at execution time.
		;(window as any).abwaabChat = {
			...identity,
			side: direction === 'rtl' ? 'left' : 'right',
			bottomOffset: MOBILE_BOTTOM_OFFSET,
			bottomOffsetWide: DESKTOP_BOTTOM_OFFSET,
		}

		// Reuse an existing tag if a previous mount already added it (embed.js
		// itself also guards on the frame id, so double-append is harmless but
		// pointless). Marked data-waselchat so teardown can find it.
		const src = `${chat.baseUrl}/embed.js`
		const existing = document.querySelector(`script[src="${src}"]`) as HTMLScriptElement | null
		if (!existing) {
			const script = document.createElement('script')
			script.src = src
			script.defer = true
			script.setAttribute('data-waselchat', 'true')
			document.body.appendChild(script)
		}
		// No teardown on unmount/dep-change: the frame outliving route changes is
		// the point. Removal happens only on the explicit !active branch above —
		// and a direction change falls through to identify(), keeping the side
		// the widget mounted with rather than tearing down an open conversation.
	}, [active, chat?.externalId, chat?.identifierHash, chat?.baseUrl, direction])

	return null
}

export default WaselChat
